const article = {
  slug: "listing-appointment-checklist",
  cluster: "agent",
  title: "The Listing Appointment Checklist: What to Bring to Win (2026)",
  description:
    "The complete listing appointment checklist for real estate agents: what to do before, during, and after the appointment, and the three deliverables to bring to win the listing.",
  audience: "Real estate agents",
  funnelStage: "MOFU",
  datePublished: "2026-06-25",
  dateModified: "2026-06-25",
  readingTime: "8 min read",
  tldr: "To win a listing appointment, bring three deliverables: a defensible CMA, a pre-listing readiness assessment of the home, and a written marketing plan. Before the appointment, research the property and seller and prepare your materials. During it, listen first, present price last, and let your plan do the talking. After, follow up within 24 hours with everything in writing.",
  blocks: [
    {
      type: "p",
      spans: [
        "Listing appointments are won on preparation, not charisma. The agent who shows up with a defensible price, a clear-eyed read on the home's condition, and a written plan for getting it sold almost always beats the agent who shows up with a folder of testimonials and a confident smile. This is the full checklist — before, during, and after — built around the three things you should physically bring to the table.",
      ],
    },
    {
      type: "callout",
      spans: [
        "The three deliverables that win listings: (1) a CMA that defends the price, (2) a pre-listing readiness assessment of the home, and (3) a written marketing plan. Everything else on this checklist exists to support those three.",
      ],
    },
    {
      type: "h2",
      text: "What should you bring to a listing appointment?",
      id: "what-to-bring",
    },
    {
      type: "p",
      spans: [
        "Sellers are interviewing you to answer three questions: What's it worth? What's wrong with it? And how will you sell it? Bring one deliverable for each. When you walk in with all three already prepared, you've answered the interview before the seller has finished asking.",
      ],
    },
    {
      type: "table",
      headers: ["Deliverable", "Question it answers", "What it includes"],
      rows: [
        [
          "Comparative market analysis (CMA)",
          "What is my home worth?",
          "Recent sold comps, active competition, a defensible price range, and the data behind it",
        ],
        [
          "Pre-listing readiness assessment",
          "What's wrong with it / what should we fix?",
          "Honest read on condition, staging, photo-readiness, and quick pre-list improvements",
        ],
        [
          "Marketing plan",
          "How will you actually sell it?",
          "Pricing strategy, photo and description plan, syndication, launch timing, and open-house plan",
        ],
      ],
    },
    {
      type: "p",
      spans: [
        "Most agents bring the CMA. Fewer bring a real marketing plan. Almost nobody brings a pre-listing readiness assessment — which is exactly why it sets you apart. For a deeper play-by-play on the conversation itself, see ",
        {
          text: "how to win the listing appointment",
          href: "/resources/how-to-win-the-listing-appointment",
        },
        ".",
      ],
    },
    {
      type: "h2",
      text: "What to do before the listing appointment",
      id: "before-the-appointment",
    },
    {
      type: "p",
      spans: [
        "The work that wins the listing happens before you ring the doorbell. Aim to do this the day before, not in the car outside.",
      ],
    },
    {
      type: "ol",
      items: [
        [
          "Pull the property record. Beds, baths, square footage, lot size, year built, tax history, and prior sales. Note any discrepancies between public records and what the seller told you.",
        ],
        [
          "Build the CMA. Pull recent sold comps (the last 3–6 months), the active competition the home will face, and any expired or withdrawn listings nearby. Arrive with a defensible price range, not a single number. See ",
          {
            text: "how to price a listing with a CMA",
            href: "/resources/how-to-price-a-listing-cma",
          },
          ".",
        ],
        [
          "Research the seller's motivation. Why are they selling, and when do they need to be out? Are they also buying? Has the home been listed before? Motivation shapes everything from price to timeline.",
        ],
        [
          "Drive the neighborhood and preview the competition. Know what buyers will compare this home to. If a comparable listing is open, walk it.",
        ],
        [
          "Prepare your pre-listing readiness assessment framework. You can't finish it until you see the home, but go in knowing exactly what you'll evaluate: condition, clutter, lighting, curb appeal, and photo-readiness.",
        ],
        [
          "Assemble your pre-listing package. Your CMA, marketing plan, a one-page bio or track record, the listing agreement, and any required disclosure forms. A reusable ",
          {
            text: "pre-listing package template",
            href: "/resources/pre-listing-package-template",
          },
          " makes this repeatable.",
        ],
        [
          "Confirm the appointment and ask who will be present. You want every decision-maker in the room. Presenting price to one spouse who then has to re-sell it to the other is a lost listing waiting to happen.",
        ],
      ],
    },
    {
      type: "callout",
      spans: [
        "Bring two copies of everything. When both decision-makers can hold the CMA and the plan in their own hands, you're presenting to a team — not performing for an audience.",
      ],
    },
    {
      type: "h2",
      text: "What to do during the listing appointment",
      id: "during-the-appointment",
    },
    {
      type: "p",
      spans: [
        "The single most common mistake agents make is leading with price. Walk the home and listen first. Present your number only after the seller trusts that you understand their home and their goals — otherwise the price is just an opinion they can argue with.",
      ],
    },
    {
      type: "ol",
      items: [
        [
          "Tour the home and let the seller talk. Ask what they love about it and what they've updated. You're gathering material for both your CMA adjustments and your marketing story.",
        ],
        [
          "Take notes for your readiness assessment as you go. Where's the clutter? Is the lighting working in your favor? What's the first photo going to be? Note quick wins the seller can do before listing.",
        ],
        [
          "Confirm the seller's timeline and motivation out loud. Restate what you heard so they know you were listening.",
        ],
        [
          "Present the marketing plan before the price. Show them how you'll make the home look its best, where it will be seen, and when you'll launch. This earns you the right to deliver the price.",
        ],
        [
          "Present the CMA and the price range — and defend it with comps, not adjectives. Walk them through the sold comps and the active competition. Let the data carry the number.",
        ],
        [
          "Deliver the readiness assessment honestly but constructively. 'Here are three things that will help this home show better and likely sell for more.' Honesty here builds more trust than flattery ever will.",
        ],
        [
          "Handle the 'why are you better than the other agent' question with your plan, not your ego. Point back to the written marketing plan and the assessment. Specifics beat superlatives.",
        ],
        [
          "Ask for the business. Then stop talking. 'I'd love to represent you on this. Are you ready to move forward?' Silence after the ask is your friend.",
        ],
      ],
    },
    {
      type: "stat",
      value: "Present the plan before the price.",
      label:
        "Price is an opinion until the seller trusts that you understand their home. The marketing plan and readiness assessment earn that trust first.",
    },
    {
      type: "h2",
      text: "What goes in the marketing plan you bring?",
      id: "the-marketing-plan",
    },
    {
      type: "p",
      spans: [
        "A marketing plan is the deliverable that separates you from the agent who only brought a price. It should be specific enough that the seller can picture exactly what will happen after they sign. A strong ",
        {
          text: "listing marketing plan",
          href: "/resources/listing-marketing-plan",
        },
        " covers the full arc from prep to launch.",
      ],
    },
    {
      type: "ul",
      items: [
        [
          "Pricing strategy — the range, the recommended launch price, and how you'll respond to market feedback in the first two weeks.",
        ],
        [
          "Presentation — professional photography, staging or decluttering recommendations, and a ",
          {
            text: "listing description written to sell",
            href: "/resources/how-to-write-a-listing-description-that-sells",
          },
          " rather than to merely describe.",
        ],
        ["Photo plan — the lead photo, the shot list, and the order the photos will run in."],
        [
          "Distribution — where the listing will be syndicated, the coming-soon push, and any agent-to-agent outreach.",
        ],
        ["Launch timing — going live to catch the weekend traffic spike, not quietly mid-week."],
        ["Open house and showing plan — what you'll do in the critical first weekend."],
        [
          "Communication — how often you'll report back and what metrics you'll share (views, showings, feedback).",
        ],
      ],
    },
    {
      type: "h2",
      text: "What to do after the listing appointment",
      id: "after-the-appointment",
    },
    {
      type: "p",
      spans: [
        "Whether you got a yes, a maybe, or a no, the follow-up matters. The agent who follows up fast and in writing often wins the listing the in-room agent thought they'd already lost.",
      ],
    },
    {
      type: "ol",
      items: [
        [
          "Send a same-day thank-you. Short, specific, and referencing something from the conversation.",
        ],
        [
          "Put everything in writing within 24 hours. Email the CMA summary, the marketing plan, and the readiness recommendations so the seller can review them — and so they have something concrete to compare against any other agent.",
        ],
        [
          "If you got the listing: confirm next steps, schedule the photographer, and send the readiness checklist so prep starts immediately.",
        ],
        [
          "If you got a 'we need to think about it': agree on a specific follow-up date before you leave, then honor it. Don't disappear.",
        ],
        [
          "If you lost it or it's an expired/FSBO situation: stay in the pipeline. Many listings come back around, and a thoughtful follow-up sequence wins more than persistence alone. See ",
          {
            text: "how to win expired listings",
            href: "/resources/how-to-win-expired-listings",
          },
          ".",
        ],
        [
          "Debrief yourself. What objection caught you flat-footed? Tighten that part of your presentation before the next one.",
        ],
      ],
    },
    {
      type: "h2",
      text: "How to prepare all three deliverables faster",
      id: "prep-faster",
    },
    {
      type: "p",
      spans: [
        "The reason most agents skip the readiness assessment and bring a thin marketing plan is time — full prep for a single appointment can eat an afternoon. Listino's Pre-Listing Prep is built for exactly this: import a Zillow URL or enter the details, and get a same-day listing prep with a comparative market analysis, a condition and presentation read, a description rewrite, and photo recommendations — the raw material for all three deliverables. It turns 'prep less, win more' from a slogan into your actual workflow. ",
        {
          text: "Get started here",
          href: "/get-started",
        },
        ".",
      ],
    },
  ],
  faqs: [
    {
      question: "What is the most important thing to bring to a listing appointment?",
      answer: [
        {
          type: "p",
          spans: [
            "A defensible CMA is the single most important item, because the seller's first question is always 'what is my home worth?' But the agents who consistently win bring two more things alongside it: an honest pre-listing readiness assessment of the home's condition, and a written marketing plan showing exactly how the home will be sold. Most agents bring only the price. Bringing all three answers the seller's interview before it starts.",
          ],
        },
      ],
    },
    {
      question: "Should I present the price first or last at a listing appointment?",
      answer: [
        {
          type: "p",
          spans: [
            "Present the price last. If you lead with a number before the seller trusts that you understand their home and goals, the price becomes just an opinion they can argue with. Tour the home, listen to their motivation, then present your marketing plan. Once the seller sees how you'll sell the home, you've earned the right to deliver the price — and the CMA defends it with comps rather than adjectives.",
          ],
        },
      ],
    },
    {
      question: "How do I follow up after a listing appointment?",
      answer: [
        {
          type: "p",
          spans: [
            "Send a same-day thank-you, then put everything in writing within 24 hours — the CMA summary, the marketing plan, and your readiness recommendations. If the seller said 'let us think about it,' agree on a specific follow-up date before you leave the appointment and honor it. Fast, written, specific follow-up frequently wins listings that the agent in the room assumed were already decided.",
          ],
        },
      ],
    },
    {
      question: "What is a pre-listing readiness assessment?",
      answer: [
        {
          type: "p",
          spans: [
            "It's an honest, constructive read on the home's condition and presentation: clutter, lighting, curb appeal, staging, needed repairs, and how photo-ready the home is. You bring it to the appointment as 'here are three things that will help this home show better and likely sell for more.' Almost no competing agent brings one, so it sets you apart and builds more trust than flattery — and it gives the seller a concrete pre-list action plan.",
          ],
        },
      ],
    },
    {
      question: "How long does it take to prepare for a listing appointment?",
      answer: [
        {
          type: "p",
          spans: [
            "Done well, full prep — pulling the property record, building the CMA, researching the seller, previewing competition, and assembling a marketing plan and pre-listing package — can take a few hours. That time cost is why many agents cut corners on the marketing plan and skip the readiness assessment. Using a tool that prepares a same-day plan with the CMA, condition read, and presentation recommendations compresses most of that prep so you can walk in with all three deliverables ready.",
          ],
        },
      ],
    },
  ],
  related: [
    "how-to-win-the-listing-appointment",
    "how-to-price-a-listing-cma",
    "pre-listing-package-template",
    "listing-marketing-plan",
  ],
};

export default article;
